import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BASE_STRINGING_PRICE = 20;
const LESSON_DISCOUNT = 5;
const STENCIL_PRICE = 1;

const AGREEMENT_VERSION = "1.0";
const AGREEMENT_EFFECTIVE_DATE = "2026-07-14";

const RACQUET_SERVICE_AGREEMENT = `
FRAMEWORK TENNIS
THE LAB
FRAMEWORK RACQUET SERVICE AGREEMENT

Version 1.0 | Effective Date: July 14, 2026

IMPORTANT NOTICE

Please read this agreement carefully before authorizing racquet service. By accepting electronically or submitting a service request, you agree to the terms contained in this document.

This Framework Racquet Service Agreement ("Agreement") is entered into between the customer placing or authorizing a racquet-service order ("Customer") and Framework Tennis, including its owners, employees, independent contractors, racquet technicians, agents, delivery personnel, successors, assigns, and any subsequently formed entity operating the business under the Framework Tennis name (collectively, "Framework"). By checking the acceptance box, submitting an order, signing electronically, or otherwise authorizing work, Customer confirms that Customer has read, understood, and voluntarily agrees to this Agreement.

1. Scope of Services

Framework may provide racquet stringing, restringing, hybrid stringing, grip and overgrip installation, grip build-up, butt-cap or end-cap service, grommet and bumper replacement, frame cleaning, weight and balance customization, racquet inspection, product installation, pickup, delivery, and related racquet services selected or approved by Customer.

Only services and products shown in the written or electronic order, or later approved by Customer, are authorized. Framework may decline any request that it believes cannot be completed safely, lawfully, accurately, or within available time and inventory constraints.

2. Customer Information and Authority

Customer represents that all contact, racquet, product, tension, scheduling, and delivery information supplied is accurate and complete. Customer further represents that Customer owns the racquet or has authority from the owner to request the services.

Customer is responsible for reviewing the order before submission, including racquet identity, string selection, gauge, color, main and cross tension, hybrid orientation, grip size, add-ons, and special instructions.

3. Authorization to Inspect and Perform Work

Customer authorizes Framework to inspect the racquet and perform the requested services. Framework may pause work and contact Customer if it discovers a cracked, warped, excessively worn, previously repaired, counterfeit, altered, heat-damaged, water-damaged, or otherwise questionable frame, grommet system, handle, pallet, butt cap, or component.

Unless Customer separately approves additional work, Framework will not knowingly substitute a materially different product or perform an unrequested paid service. Minor consumables or ordinary installation materials reasonably necessary to complete an approved service may be used.

4. Racquet Condition and Pre-Existing Damage

Customer acknowledges that used racquets may contain hidden wear, stress fractures, weakened graphite, damaged grommets, brittle plastics, loose handles, internal defects, prior modifications, or manufacturer defects that are not visible during ordinary inspection.

Framework is not responsible for damage, failure, breakage, deformation, paint loss, decal wear, grommet failure, handle separation, or other loss caused by normal wear, age, misuse, prior work, hidden defects, manufacturer defects, counterfeit construction, improper storage, or a pre-existing condition that was not caused by Framework.

5. Stringing, Tension, and Performance Changes

Customer understands that stringing and customization change racquet feel, stiffness, power, control, spin, comfort, sound, weight, balance, and playability. Requested tension is a target setting on the stringing machine and is not a guarantee of measured string-bed stiffness or identical performance across racquets, machines, string types, gauges, temperatures, or time.

Strings may lose tension, notch, fray, discolor, move, or break through ordinary use. Natural gut and some specialty strings are particularly sensitive to moisture, handling, installation, and environmental conditions.

Customer assumes responsibility for the selection of string, tension, hybrid configuration, grip, weight, balance, and other customer-directed specifications unless Framework expressly provides a written recommendation.

6. Products, Inventory, and Special Orders

In-stock status is subject to change. An item displayed as out of stock cannot be added to immediate checkout. A special-order request is an inquiry only and does not create an order, reserve inventory, or guarantee availability, pricing, or delivery time.

Framework will attempt to confirm availability, estimated timing, and pricing before requesting payment for a special-order item. Unless Framework agrees otherwise in writing, special-order products are ordered only after payment or other express approval and may be noncancelable or nonrefundable once ordered from a supplier.

Expedited service is limited to eligible services and currently available inventory. Out-of-stock and special-order products are not eligible for expedited completion.

7. Turnaround and Expedited Requests

All turnaround times are estimates unless Framework expressly confirms a guaranteed completion time in writing. Standard turnaround depends on workload, technician availability, inventory, racquet condition, and transfer logistics.

An expedited request does not guarantee same-day, next-day, or any particular completion time. Availability and pricing may depend on who has the racquet, the work requested, when it is needed, where transfer must occur, and available pickup or delivery options. Any expedited fee not shown at checkout will be communicated for Customer approval before work begins.

8. Pickup, Drop-Off, Delivery, and Transfer

Customer is responsible for providing accurate transfer instructions and for being available at an agreed time and location. Free or discounted transfer options may be limited to scheduled lessons or designated Framework locations and may change based on the coaching and service schedule.

Pickup, delivery, or separate meet-up service is subject to distance, route, timing, and personnel availability. Providing multiple possible dates, times, and locations may improve scheduling and pricing. Travel charges not shown at checkout must be approved before they are added.

Customer assumes the ordinary risks of transportation and transfer, including delays, weather, traffic, theft by third parties, or loss outside Framework's reasonable control. Framework may use an owner, employee, independent contractor, racquet technician, or delivery person to transport an item.

9. Inspection on Return and Service Concerns

Customer should inspect the racquet promptly when it is returned. Any concern relating to visible damage, incorrect product, incorrect tension entry, missing item, or incomplete service should be reported to Framework as soon as reasonably possible, preferably within forty-eight (48) hours.

Framework must be given a reasonable opportunity to inspect the racquet and, when appropriate, correct the service before Customer seeks service elsewhere. This paragraph does not eliminate any nonwaivable legal right.

10. Payment, Quotes, and Refunds

Customer agrees to pay all approved charges, taxes, travel fees, product costs, and service fees. Estimates and quotes may change if Customer changes the request or Framework discovers a condition requiring additional work; no unapproved additional charge will be incurred without Customer authorization.

Completed labor is generally nonrefundable. Installed, cut, opened, customized, or specially ordered products may be nonreturnable except where required by law or where Framework agrees that the product or service was materially incorrect or defective.

If Framework cannot perform an already-paid service, Framework may cancel the affected portion and issue an appropriate refund. Payment disputes should first be submitted directly to Framework so the parties may attempt to resolve them.

11. Storage and Unclaimed Property

Framework may notify Customer when service is complete or when an item is ready for pickup. Customer agrees to retrieve the racquet or arrange delivery within a reasonable period.

After thirty (30) days, Framework may charge a reasonable storage fee if disclosed to Customer. After repeated documented attempts to contact Customer and any additional notice required by applicable law, unclaimed property may be handled as permitted by Georgia law. Framework should obtain legal guidance before disposing of, selling, or donating unclaimed property.

12. Assumption of Risk and Release of Liability

Customer voluntarily assumes the ordinary and foreseeable risks associated with racquet stringing, customization, component replacement, transportation, and use of a serviced racquet, including changes in performance, string or component failure, aggravation of hidden defects, and ordinary transportation risks.

TO THE FULLEST EXTENT PERMITTED BY GEORGIA LAW, CUSTOMER RELEASES AND HOLDS HARMLESS FRAMEWORK FROM CLAIMS FOR PROPERTY DAMAGE, LOSS, DELAY, OR PERSONAL INJURY ARISING FROM THE ORDINARY RISKS DESCRIBED IN THIS AGREEMENT OR FROM FRAMEWORK'S ORDINARY NEGLIGENCE IN PROVIDING THE REQUESTED SERVICES.

THIS RELEASE DOES NOT APPLY TO GROSS NEGLIGENCE, RECKLESSNESS, WILLFUL OR WANTON MISCONDUCT, FRAUD, INTENTIONAL MISCONDUCT, OR ANY LIABILITY THAT CANNOT LAWFULLY BE WAIVED.

13. Limitation of Remedies

To the fullest extent permitted by law, Framework's total liability arising from a racquet-service transaction will not exceed the lesser of: (a) the reasonable cost to correct the affected service; (b) the fair market value of the specific racquet or product directly damaged by Framework; or (c) the amount otherwise required by applicable law.

Framework will not be liable for indirect, incidental, special, exemplary, or consequential damages, including lost playing time, missed matches, tournament expenses, travel expenses, lost income, lost ranking opportunities, or emotional distress, except where such limitation is prohibited by law.

14. Communications Consent

Customer authorizes Framework to contact Customer at the email address and telephone number provided regarding the service request, clarification of specifications, quotes, payment requests, scheduling, order updates, inventory, pickup, drop-off, delivery, and post-service concerns.

Where Customer selects text messaging, Customer consents to receive service-related text messages. Message and data rates may apply. Customer may request that Framework stop using a particular communication method, but Framework may require another practical method to complete or coordinate the transaction.

Consent to service-related communications is separate from consent to receive advertising or promotional marketing. Framework should obtain any additional consent required before sending automated or promotional communications.

15. Privacy and Records

Framework may retain order details, contact information, acceptance records, timestamps, communications, photographs of the racquet, and service notes for business, quality-control, payment, insurance, and legal purposes.

Framework will not sell Customer's personal information. Information may be disclosed to payment processors, suppliers, delivery providers, professional advisers, insurers, service vendors, or governmental authorities when reasonably necessary to complete the transaction, operate the business, comply with law, or protect legal rights.

16. Electronic Acceptance

Customer agrees to conduct this transaction electronically. Checking an acceptance box, entering Customer's name, clicking a submission or payment button, or otherwise electronically authorizing the order is intended to serve as Customer's electronic signature.

Framework may store the accepted agreement version, date and time, order information, and available technical records associated with acceptance. Customer may request a copy of the Agreement by email.

17. Governing Law; Venue

This Agreement is governed by the laws of the State of Georgia, without regard to conflict-of-law principles. To the extent legally permitted, any action relating to this Agreement shall be brought in a court of competent jurisdiction located in Georgia in the county where Framework's principal business operations are then located, unless the parties agree otherwise in writing.

18. Severability; No Waiver; Entire Agreement

If any provision of this Agreement is found invalid or unenforceable, that provision shall be enforced to the maximum extent permitted and the remaining provisions shall remain in effect.

Framework's failure to enforce a provision on one occasion is not a waiver of that provision. This Agreement, together with the customer's order, approved quote, and any specifically incorporated policy, constitutes the complete agreement concerning the racquet-service transaction and supersedes prior oral statements concerning that transaction.

19. Customer Acknowledgment

BY ACCEPTING THIS AGREEMENT, CUSTOMER ACKNOWLEDGES THAT CUSTOMER: (A) HAS READ THE ENTIRE AGREEMENT; (B) UNDERSTANDS THAT IT INCLUDES AN ASSUMPTION OF RISK, RELEASE OF LIABILITY, AND LIMITATION OF REMEDIES; (C) HAS HAD THE OPPORTUNITY TO ASK QUESTIONS; (D) IS AT LEAST EIGHTEEN (18) YEARS OLD OR IS THE PARENT OR LEGAL GUARDIAN AUTHORIZING THE TRANSACTION; AND (E) VOLUNTARILY AGREES TO BE BOUND.

FRAMEWORK TENNIS — THE LAB

Questions regarding this agreement or a service order should be directed to the contact information published on frameworktennis.com.
`.trim();

const EMPTY_FORM = {
    name: "",
    email: "",
    phone: "",

    communicationConsent: false,

    racquetBrand: "",
    racquetModel: "",

    serviceSpeed: "standard",

    stringOption: "included-synthetic",
    stringProductId: "",
    customerStringDetails: "",

    tensionMain: 52,
    tensionCross: 50,

    overgripOption: "none",
    installedOvergripId: "",
    overgripPackId: "",

    dampenerOption: "none",
    dampenerId: "",

    stencilOption: "none",
    stencilDesign: "",
    stencilColor: "",

    handoffMethod: "lesson",
    handoffDetails: "",

    advancedOpen: false,

    grommetService: "none",
    grommetDetails: "",

    replacementGripService: "none",
    replacementGripId: "",
    customerGripDetails: "",

    buttCapService: "none",
    buttCapDetails: "",

    leadTapeRequest: false,
    leadTapeDetails: "",

    performanceSession: false,

    notes: "",
    termsAccepted: false,
};

export default function FrameworkStringing() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [inventory, setInventory] = useState([]);
    const [loadingInventory, setLoadingInventory] = useState(true);
    const [busy, setBusy] = useState(false);
    const [waiverOpen, setWaiverOpen] = useState(false);

    useEffect(() => {
        loadInventory();
    }, []);

    async function loadInventory() {
        setLoadingInventory(true);

        try {
            /*
             * Update this endpoint if your existing product API differs.
             *
             * Recommended product fields:
             * id
             * name
             * category
             * price
             * quantity
             * stock_status
             * active
             * eligible_for_expedited
             */
            const response = await api.get(
                "/products?brand=framework&active=true"
            );

            const products =
                response?.data?.products ??
                response?.data ??
                [];

            setInventory(Array.isArray(products) ? products : []);
        } catch (error) {
            console.error("Inventory failed to load:", error);
            toast.error("Current inventory could not be loaded.");
            setInventory([]);
        } finally {
            setLoadingInventory(false);
        }
    }

    function update(field, value) {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));
    }

    const strings = useMemo(
        () => productsByCategory(inventory, "string"),
        [inventory]
    );

    const syntheticGutStrings = useMemo(
        () =>
            strings.filter(
                (item) =>
                    isInStock(item) &&
                    (
                        normalize(item.subcategory) === "synthetic_gut" ||
                        normalize(item.string_type) === "synthetic_gut" ||
                        normalize(item.name).includes("synthetic_gut")
                    )
            ),
        [strings]
    );

    const premiumStrings = useMemo(
        () =>
            strings.filter((item) => {
                if (!isInStock(item)) return false;

                const isSynthetic =
                    normalize(item.subcategory) === "synthetic_gut" ||
                    normalize(item.string_type) === "synthetic_gut" ||
                    normalize(item.name).includes("synthetic_gut");

                return !isSynthetic;
            }),
        [strings]
    );

    const installedOvergrips = useMemo(
        () =>
            inventory.filter(
                (item) =>
                    normalize(item.category) === "overgrip" &&
                    isInStock(item) &&
                    item.available_for_install !== false
            ),
        [inventory]
    );

    const overgripPacks = useMemo(
        () =>
            inventory.filter(
                (item) =>
                    normalize(item.category) === "overgrip_pack_3" &&
                    isInStock(item)
            ),
        [inventory]
    );

    const dampeners = useMemo(
        () =>
            inventory.filter(
                (item) =>
                    normalize(item.category) === "dampener" &&
                    isInStock(item)
            ),
        [inventory]
    );

    const replacementGrips = useMemo(
        () =>
            inventory.filter(
                (item) =>
                    normalize(item.category) === "replacement_grip" &&
                    isInStock(item)
            ),
        [inventory]
    );

    const selectedString = findProduct(
        premiumStrings,
        form.stringProductId
    );

    const selectedInstalledOvergrip = findProduct(
        installedOvergrips,
        form.installedOvergripId
    );

    const selectedOvergripPack = findProduct(
        overgripPacks,
        form.overgripPackId
    );

    const selectedDampener = findProduct(
        dampeners,
        form.dampenerId
    );

    const selectedReplacementGrip = findProduct(
        replacementGrips,
        form.replacementGripId
    );

    const totals = useMemo(() => {
        let subtotal = BASE_STRINGING_PRICE;

        if (form.stringOption === "premium") {
            subtotal += getEffectivePrice(selectedString);
        }

        if (form.overgripOption === "installed") {
            subtotal += getEffectivePrice(selectedInstalledOvergrip);
        }

        if (form.overgripOption === "pack") {
            subtotal += getEffectivePrice(selectedOvergripPack);
        }

        if (form.dampenerOption === "installed") {
            subtotal += getEffectivePrice(selectedDampener);
        }

        if (form.stencilOption === "add") {
            subtotal += STENCIL_PRICE;
        }

        if (form.grommetService === "customer-provided") {
            subtotal += 10;
        }

        if (form.replacementGripService === "customer-provided") {
            subtotal += 5;
        }

        if (form.replacementGripService === "framework-stock") {
            subtotal += getEffectivePrice(selectedReplacementGrip);
        }

        if (form.buttCapService === "customer-provided") {
            subtotal += 10;
        }

        if (form.performanceSession) {
            subtotal += 100;
        }

        const discount =
            form.handoffMethod === "lesson"
                ? LESSON_DISCOUNT
                : 0;

        return {
            subtotal,
            discount,
            total: Math.max(0, subtotal - discount),
        };
    }, [
        form,
        selectedString,
        selectedInstalledOvergrip,
        selectedOvergripPack,
        selectedDampener,
        selectedReplacementGrip,
    ]);

    function openSpecialOrder(type = "general") {
        const params = new URLSearchParams({
            type: "special-order",
            source: "the-lab",
            item_type: type,
            name: form.name,
            email: form.email,
            phone: form.phone,
        });

        window.location.href =
            `/framework/contact?${params.toString()}`;
    }

    function validate() {
        if (!form.name.trim()) {
            toast.error("Please enter your name.");
            return false;
        }

        if (!form.email.trim()) {
            toast.error("Please enter your email address.");
            return false;
        }

        if (!form.phone.trim()) {
            toast.error("Please enter your phone number.");
            return false;
        }

        if (!form.communicationConsent) {
            toast.error(
                "Communication consent is required so we can contact you about the order."
            );
            return false;
        }

        if (!form.racquetBrand.trim() || !form.racquetModel.trim()) {
            toast.error("Please identify your racquet.");
            return false;
        }

        if (
            form.stringOption === "premium" &&
            !form.stringProductId
        ) {
            toast.error("Please select a premium string.");
            return false;
        }

        if (
            form.stringOption === "customer-provided" &&
            !form.customerStringDetails.trim()
        ) {
            toast.error("Please identify the string you are providing.");
            return false;
        }

        if (
            form.overgripOption === "installed" &&
            !form.installedOvergripId
        ) {
            toast.error("Please choose an overgrip to install.");
            return false;
        }

        if (
            form.overgripOption === "pack" &&
            !form.overgripPackId
        ) {
            toast.error("Please choose a three-pack.");
            return false;
        }

        if (
            form.dampenerOption === "installed" &&
            !form.dampenerId
        ) {
            toast.error("Please choose a dampener.");
            return false;
        }

        if (
            form.stencilOption === "add" &&
            (!form.stencilDesign || !form.stencilColor)
        ) {
            toast.error("Please choose a stencil and ink color.");
            return false;
        }

        if (
            form.handoffMethod === "special-request" &&
            !form.handoffDetails.trim()
        ) {
            toast.error(
                "Please provide possible dates, times, and locations."
            );
            return false;
        }

        if (!form.termsAccepted) {
            toast.error(
                "You must accept the Racquet Service Agreement before continuing."
            );
            return false;
        }

        return true;
    }

    async function submit(event) {
        event.preventDefault();

        if (!validate()) return;

        setBusy(true);

        try {
            const payload = {
                brand: "framework",

                name: form.name,
                email: form.email,
                phone: form.phone,

                communication_consent:
                    form.communicationConsent,

                racquet_brand: form.racquetBrand,
                racquet_model: form.racquetModel,

                tension_main: Number(form.tensionMain),
                tension_cross: Number(form.tensionCross),

                service_speed: form.serviceSpeed,

                string_option: form.stringOption,
                string_product_id:
                    form.stringProductId || null,
                customer_string_details:
                    form.customerStringDetails,

                overgrip_option: form.overgripOption,
                installed_overgrip_id:
                    form.installedOvergripId || null,
                overgrip_pack_id:
                    form.overgripPackId || null,

                dampener_option: form.dampenerOption,
                dampener_id: form.dampenerId || null,

                stencil_option: form.stencilOption,
                stencil_design: form.stencilDesign,
                stencil_color: form.stencilColor,

                handoff_method: form.handoffMethod,
                handoff_details: form.handoffDetails,

                grommet_service: form.grommetService,
                grommet_details: form.grommetDetails,

                replacement_grip_service:
                    form.replacementGripService,
                replacement_grip_id:
                    form.replacementGripId || null,
                customer_grip_details:
                    form.customerGripDetails,

                butt_cap_service: form.buttCapService,
                butt_cap_details: form.buttCapDetails,

                lead_tape_request: form.leadTapeRequest,
                lead_tape_details: form.leadTapeDetails,

                performance_customization_session:
                    form.performanceSession,

                notes: form.notes,

                base_price: BASE_STRINGING_PRICE,
                discount: totals.discount,
                estimated_total: totals.total,

                agreement_version: AGREEMENT_VERSION,
                agreement_effective_date: AGREEMENT_EFFECTIVE_DATE,
                terms_accepted: form.termsAccepted,

                // Legacy fields required by the current FastAPI backend.
                // Extra fields above are preserved in customization_notes
                // so the complete request is still available in admin.
                string_choice:
                    form.stringOption === "premium"
                        ? selectedString?.name || "Premium string"
                        : form.stringOption === "customer-provided"
                          ? form.customerStringDetails
                          : syntheticGutStrings[0]?.name ||
                            "Included synthetic gut",
                turnaround: form.serviceSpeed,
                customization_notes: JSON.stringify({
                    communication_consent: form.communicationConsent,
                    string_option: form.stringOption,
                    string_product_id: form.stringProductId || null,
                    customer_string_details: form.customerStringDetails,
                    overgrip_option: form.overgripOption,
                    installed_overgrip_id: form.installedOvergripId || null,
                    overgrip_pack_id: form.overgripPackId || null,
                    dampener_option: form.dampenerOption,
                    dampener_id: form.dampenerId || null,
                    stencil_option: form.stencilOption,
                    stencil_design: form.stencilDesign,
                    stencil_color: form.stencilColor,
                    handoff_method: form.handoffMethod,
                    handoff_details: form.handoffDetails,
                    grommet_service: form.grommetService,
                    grommet_details: form.grommetDetails,
                    replacement_grip_service: form.replacementGripService,
                    replacement_grip_id: form.replacementGripId || null,
                    customer_grip_details: form.customerGripDetails,
                    butt_cap_service: form.buttCapService,
                    butt_cap_details: form.buttCapDetails,
                    lead_tape_request: form.leadTapeRequest,
                    lead_tape_details: form.leadTapeDetails,
                    performance_customization_session: form.performanceSession,
                    notes: form.notes,
                    base_price: BASE_STRINGING_PRICE,
                    discount: totals.discount,
                    estimated_total: totals.total,
                    agreement_version: AGREEMENT_VERSION,
                    agreement_effective_date: AGREEMENT_EFFECTIVE_DATE,
                    terms_accepted: form.termsAccepted,
                }),
            };

            const response = await api.post(
                "/stringing",
                payload
            );

            const checkoutUrl =
                response?.data?.checkout_url ??
                response?.checkout_url;

            if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
            }

            toast.success(
                "Your racquet-service order was received."
            );

            setForm(EMPTY_FORM);
        } catch (error) {
            console.error("Stringing submission failed:", error);

            const detail = error?.response?.data?.detail;

            const message = Array.isArray(detail)
                ? detail
                      .map((item) => {
                          const field = item?.loc?.at(-1);
                          return field
                              ? `${field}: ${item.msg}`
                              : item.msg;
                      })
                      .join(" • ")
                : detail ||
                  error?.response?.data?.message ||
                  "Submission failed. Please try again.";

            toast.error(message);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="framework-stringing-page">
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-12">
                <h1 className="fw-display text-5xl sm:text-7xl">
                    The Lab{" "}
                    <span className="text-[#a6ce39]">
                        by Framework.
                    </span>
                </h1>

                <p className="mt-6 text-white/60 max-w-3xl text-lg">
                    Professional stringing, accessories, repairs,
                    and performance racquet customization.
                </p>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
                <form
                    onSubmit={submit}
                    className="space-y-10 border border-white/10 p-6 md:p-8 bg-[#0f0f10]"
                >
                    <FormSection
                        number="01"
                        title="Contact information"
                    >
                        <div className="grid md:grid-cols-2 gap-6">
                            <Field
                                label="Name *"
                                value={form.name}
                                onChange={(value) =>
                                    update("name", value)
                                }
                                testid="fw-str-name"
                                required
                            />

                            <Field
                                label="Email *"
                                type="email"
                                value={form.email}
                                onChange={(value) =>
                                    update("email", value)
                                }
                                testid="fw-str-email"
                                required
                            />

                            <Field
                                label="Mobile phone *"
                                type="tel"
                                value={form.phone}
                                onChange={(value) =>
                                    update("phone", value)
                                }
                                testid="fw-str-phone"
                                required
                            />
                        </div>

                        <div className="mt-6">
                            <Checkbox
                                checked={
                                    form.communicationConsent
                                }
                                onChange={(checked) =>
                                    update(
                                        "communicationConsent",
                                        checked
                                    )
                                }
                                testid="fw-str-communication-consent"
                                label="I consent to receive emails, text messages, and/or phone calls from Framework regarding this order, scheduling, pickup or delivery, payment, and service updates. This consent is required so Framework can contact me about my order."
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        number="02"
                        title="Your racquet"
                    >
                        <div className="grid md:grid-cols-2 gap-6">
                            <Field
                                label="Racquet brand *"
                                value={form.racquetBrand}
                                onChange={(value) =>
                                    update("racquetBrand", value)
                                }
                                testid="fw-str-brand"
                                required
                            />

                            <Field
                                label="Racquet model *"
                                value={form.racquetModel}
                                onChange={(value) =>
                                    update("racquetModel", value)
                                }
                                testid="fw-str-model"
                                required
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        number="03"
                        title="Standard stringing"
                    >
                        <div className="border border-[#a6ce39]/40 bg-[#a6ce39]/5 p-6">
                            <div className="flex flex-wrap justify-between gap-4">
                                <div>
                                    <h3 className="text-xl font-semibold">
                                        Standard Racquet Stringing
                                    </h3>

                                    <p className="text-white/60 mt-2">
                                        Includes one currently
                                        available synthetic gut string.
                                    </p>
                                </div>

                                <div className="text-3xl fw-display text-[#a6ce39]">
                                    $20
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 grid md:grid-cols-3 gap-4">
                            <ChoiceCard
                                title="Included synthetic gut"
                                description={
                                    syntheticGutStrings.length
                                        ? `Current availability: ${syntheticGutStrings
                                              .map(
                                                  (item) =>
                                                      item.name
                                              )
                                              .join(", ")}`
                                        : "Framework will use an available synthetic gut."
                                }
                                selected={
                                    form.stringOption ===
                                    "included-synthetic"
                                }
                                onClick={() =>
                                    update(
                                        "stringOption",
                                        "included-synthetic"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Upgrade my string"
                                description="Choose an in-stock premium string below."
                                selected={
                                    form.stringOption === "premium"
                                }
                                onClick={() =>
                                    update(
                                        "stringOption",
                                        "premium"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Bring my own string"
                                selected={
                                    form.stringOption ===
                                    "customer-provided"
                                }
                                onClick={() =>
                                    update(
                                        "stringOption",
                                        "customer-provided"
                                    )
                                }
                            />
                        </div>

                        {form.stringOption === "premium" && (
                            <StringPicker
                                value={form.stringProductId}
                                onChange={(value) =>
                                    update("stringProductId", value)
                                }
                                options={premiumStrings}
                                loading={loadingInventory}
                            />
                        )}

                        {form.stringOption ===
                            "customer-provided" && (
                            <TextArea
                                label="String you are providing"
                                value={
                                    form.customerStringDetails
                                }
                                onChange={(value) =>
                                    update(
                                        "customerStringDetails",
                                        value
                                    )
                                }
                                placeholder="Brand, model, gauge, and color."
                            />
                        )}

                        <button
                            type="button"
                            onClick={() =>
                                openSpecialOrder("string")
                            }
                            className="mt-5 text-sm text-[#a6ce39] underline underline-offset-4"
                        >
                            Request a special-order string quote
                        </button>

                        <div className="grid md:grid-cols-2 gap-6 mt-8">
                            <Field
                                label="Main tension"
                                type="number"
                                value={form.tensionMain}
                                onChange={(value) =>
                                    update(
                                        "tensionMain",
                                        value
                                    )
                                }
                                testid="fw-str-main"
                            />

                            <Field
                                label="Cross tension"
                                type="number"
                                value={form.tensionCross}
                                onChange={(value) =>
                                    update(
                                        "tensionCross",
                                        value
                                    )
                                }
                                testid="fw-str-cross"
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        number="04"
                        title="Accessories"
                    >
                        <h3 className="font-semibold text-lg">
                            Overgrip
                        </h3>

                        <div className="grid md:grid-cols-3 gap-4 mt-4">
                            <ChoiceCard
                                title="No overgrip"
                                selected={
                                    form.overgripOption === "none"
                                }
                                onClick={() =>
                                    update(
                                        "overgripOption",
                                        "none"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Install one overgrip"
                                description="Installed on the racquet before return."
                                selected={
                                    form.overgripOption ===
                                    "installed"
                                }
                                onClick={() =>
                                    update(
                                        "overgripOption",
                                        "installed"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Add a three-pack"
                                description="Receive the unopened pack for future use."
                                selected={
                                    form.overgripOption === "pack"
                                }
                                onClick={() =>
                                    update(
                                        "overgripOption",
                                        "pack"
                                    )
                                }
                            />
                        </div>

                        {form.overgripOption === "installed" && (
                            <OvergripPicker
                                value={form.installedOvergripId}
                                onChange={(value) =>
                                    update("installedOvergripId", value)
                                }
                                options={installedOvergrips}
                                loading={loadingInventory}
                            />
                        )}

                        {form.overgripOption === "pack" && (
                            <SelectField
                                label="Three-pack"
                                value={form.overgripPackId}
                                onChange={(value) =>
                                    update(
                                        "overgripPackId",
                                        value
                                    )
                                }
                                options={overgripPacks}
                                loading={loadingInventory}
                            />
                        )}

                        <h3 className="font-semibold text-lg mt-8">
                            Dampener
                        </h3>

                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                            <ChoiceCard
                                title="No dampener"
                                selected={
                                    form.dampenerOption === "none"
                                }
                                onClick={() =>
                                    update(
                                        "dampenerOption",
                                        "none"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Add a dampener"
                                description="Installed before the racquet is returned."
                                selected={
                                    form.dampenerOption ===
                                    "installed"
                                }
                                onClick={() =>
                                    update(
                                        "dampenerOption",
                                        "installed"
                                    )
                                }
                            />
                        </div>

                        {form.dampenerOption === "installed" && (
                            <DampenerPicker
                                value={form.dampenerId}
                                onChange={(value) =>
                                    update("dampenerId", value)
                                }
                                options={dampeners}
                                loading={loadingInventory}
                            />
                        )}

                        <h3 className="font-semibold text-lg mt-8">
                            Stencil
                        </h3>

                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                            <ChoiceCard
                                title="No stencil"
                                description="Free"
                                selected={
                                    form.stencilOption === "none"
                                }
                                onClick={() =>
                                    update(
                                        "stencilOption",
                                        "none"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Add stencil"
                                description="+$1"
                                selected={
                                    form.stencilOption === "add"
                                }
                                onClick={() =>
                                    update(
                                        "stencilOption",
                                        "add"
                                    )
                                }
                            />
                        </div>

                        {form.stencilOption === "add" && (
                            <div className="grid md:grid-cols-2 gap-6 mt-6">
                                <SimpleSelect
                                    label="Stencil design"
                                    value={form.stencilDesign}
                                    onChange={(value) =>
                                        update(
                                            "stencilDesign",
                                            value
                                        )
                                    }
                                    options={[
                                        "Wilson",
                                        "Babolat",
                                        "HEAD",
                                        "Yonex",
                                        "Solinco",
                                        "Luxilon",
                                        "Other",
                                    ]}
                                />

                                <SimpleSelect
                                    label="Ink color"
                                    value={form.stencilColor}
                                    onChange={(value) =>
                                        update(
                                            "stencilColor",
                                            value
                                        )
                                    }
                                    options={[
                                        "Black",
                                        "White",
                                        "Red",
                                        "Blue",
                                        "Green",
                                    ]}
                                />
                            </div>
                        )}

                        <p className="text-[#a6ce39] text-sm mt-4">
                            Free Framework stencil option coming soon.
                        </p>
                    </FormSection>

                    <FormSection
                        number="05"
                        title="Racquet handoff"
                    >
                        <div className="grid md:grid-cols-3 gap-4">
                            <ChoiceCard
                                title="Bring it to my private lesson"
                                description="Free pickup and drop-off. Receive $5 off stringing services when paired with a private lesson."
                                selected={
                                    form.handoffMethod === "lesson"
                                }
                                onClick={() =>
                                    update(
                                        "handoffMethod",
                                        "lesson"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Framework location"
                                description="FREE pickup and drop-off at an available Framework coaching location."
                                selected={
                                    form.handoffMethod ===
                                    "framework-location"
                                }
                                onClick={() =>
                                    update(
                                        "handoffMethod",
                                        "framework-location"
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Request pickup or delivery"
                                description="Pricing varies based on distance, scheduling, and availability."
                                selected={
                                    form.handoffMethod ===
                                    "special-request"
                                }
                                onClick={() =>
                                    update(
                                        "handoffMethod",
                                        "special-request"
                                    )
                                }
                            />
                        </div>

                        {form.handoffMethod ===
                            "special-request" && (
                            <div className="mt-6">
                                <p className="text-white/60 text-sm mb-4">
                                    Providing several possible dates,
                                    times, and locations may result in
                                    better scheduling and rates.
                                </p>

                                <TextArea
                                    label="Possible dates, times, and locations"
                                    value={form.handoffDetails}
                                    onChange={(value) =>
                                        update(
                                            "handoffDetails",
                                            value
                                        )
                                    }
                                />
                            </div>
                        )}
                    </FormSection>

                    <FormSection
                        number="06"
                        title="Finished customizing?"
                    >
                        <div className="grid md:grid-cols-2 gap-4">
                            <ChoiceCard
                                title="Continue to checkout"
                                description="My racquet is ready."
                                selected={!form.advancedOpen}
                                onClick={() =>
                                    update(
                                        "advancedOpen",
                                        false
                                    )
                                }
                            />

                            <ChoiceCard
                                title="Advanced repairs and customization"
                                description="Grommets, grips, butt caps, lead tape, and performance fitting."
                                selected={form.advancedOpen}
                                onClick={() =>
                                    update(
                                        "advancedOpen",
                                        true
                                    )
                                }
                            />
                        </div>

                        {form.advancedOpen && (
                            <div className="mt-8 space-y-8">
                                <AdvancedRepairs
                                    form={form}
                                    update={update}
                                    replacementGrips={
                                        replacementGrips
                                    }
                                    loadingInventory={
                                        loadingInventory
                                    }
                                    openSpecialOrder={
                                        openSpecialOrder
                                    }
                                />
                            </div>
                        )}
                    </FormSection>

                    <FormSection
                        number="07"
                        title="Review"
                    >
                        <div className="border border-white/10 p-6 space-y-3">
                            <SummaryRow
                                label="Standard stringing"
                                value="$20.00"
                            />

                            {form.stringOption === "premium" && (
                                <SummaryRow
                                    label={
                                        selectedString?.name ||
                                        "Premium string"
                                    }
                                    value={`+${formatMoney(
                                        getEffectivePrice(selectedString)
                                    )}`}
                                />
                            )}

                            {form.handoffMethod === "lesson" && (
                                <SummaryRow
                                    label="Private lesson discount"
                                    value="-$5.00"
                                />
                            )}

                            <div className="border-t border-white/10 pt-4 mt-4">
                                <SummaryRow
                                    label="Estimated total"
                                    value={formatMoney(
                                        totals.total
                                    )}
                                    strong
                                />
                            </div>

                            <p className="text-xs text-white/40">
                                Special-order products, expedited
                                requests, pickup or delivery, lead-tape
                                work, and sourced repair parts may be
                                quoted separately before work begins.
                            </p>
                        </div>

                        <div className="mt-6">
                            <Checkbox
                                checked={form.termsAccepted}
                                onChange={(checked) =>
                                    update(
                                        "termsAccepted",
                                        checked
                                    )
                                }
                                testid="fw-str-terms"
                                label={
                                    <>
                                        I have read and agree to the{" "}
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                setWaiverOpen(true);
                                            }}
                                            className="text-[#a6ce39] underline underline-offset-4 hover:text-white transition-colors"
                                            data-testid="fw-str-open-agreement"
                                        >
                                            Framework Racquet Service
                                            Agreement
                                        </button>
                                        .
                                    </>
                                }
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            className="fw-btn-primary mt-6"
                            data-testid="fw-str-submit"
                        >
                            {busy
                                ? "Submitting…"
                                : "Continue to Checkout"}
                        </button>
                    </FormSection>
                </form>
            </section>

            {waiverOpen && (
                <AgreementModal
                    onClose={() => setWaiverOpen(false)}
                />
            )}
        </div>
    );
}

function AgreementModal({ onClose }) {
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        function handleKeyDown(event) {
            if (event.key === "Escape") onClose();
        }

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4 py-8"
            onClick={onClose}
            data-testid="fw-str-agreement-modal"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="racquet-service-agreement-title"
                className="relative flex max-h-[85vh] w-full max-w-2xl flex-col border border-white/15 bg-[#0f0f10] shadow-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-6 border-b border-white/10 p-6">
                    <div>
                        <div className="fw-label mb-2">
                            Framework Tennis · The Lab
                        </div>

                        <h2
                            id="racquet-service-agreement-title"
                            className="fw-display text-2xl sm:text-3xl"
                        >
                            Racquet Service Agreement
                        </h2>

                        <p className="mt-2 text-xs text-white/45">
                            Version {AGREEMENT_VERSION} · Effective July 14, 2026
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close Racquet Service Agreement"
                        className="shrink-0 text-2xl leading-none text-white/50 transition-colors hover:text-white"
                    >
                        ×
                    </button>
                </div>

                <div className="overflow-y-auto whitespace-pre-wrap px-6 py-5 text-sm leading-7 text-white/70">
                    {RACQUET_SERVICE_AGREEMENT}
                </div>

                <div className="flex justify-end border-t border-white/10 p-5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="fw-btn-primary px-5 py-2 text-xs"
                        data-testid="fw-str-close-agreement"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function AdvancedRepairs({
    form,
    update,
    replacementGrips,
    loadingInventory,
    openSpecialOrder,
}) {
    return (
        <>
            <div>
                <h3 className="text-xl font-semibold">
                    Grommet replacement
                </h3>

                <p className="text-white/55 text-sm mt-2">
                    Additional labor: $10. Parts and shipping are
                    separate.
                </p>

                <SimpleSelect
                    label="Grommet option"
                    value={form.grommetService}
                    onChange={(value) =>
                        update("grommetService", value)
                    }
                    options={[
                        {
                            value: "none",
                            label: "No grommet service",
                        },
                        {
                            value: "customer-provided",
                            label:
                                "I will provide compatible grommets — +$10 labor",
                        },
                        {
                            value: "special-order",
                            label:
                                "Framework needs to source the grommets",
                        },
                    ]}
                />

                {form.grommetService === "special-order" && (
                    <button
                        type="button"
                        onClick={() =>
                            openSpecialOrder("grommets")
                        }
                        className="mt-4 text-[#a6ce39] underline"
                    >
                        Submit grommet inquiry
                    </button>
                )}
            </div>

            <div>
                <h3 className="text-xl font-semibold">
                    Replacement grip
                </h3>

                <SimpleSelect
                    label="Grip option"
                    value={form.replacementGripService}
                    onChange={(value) =>
                        update(
                            "replacementGripService",
                            value
                        )
                    }
                    options={[
                        {
                            value: "none",
                            label: "No replacement grip",
                        },
                        {
                            value: "customer-provided",
                            label:
                                "I will provide the grip — +$5 labor",
                        },
                        {
                            value: "framework-stock",
                            label:
                                "Purchase and install an in-stock grip",
                        },
                    ]}
                />

                {form.replacementGripService ===
                    "framework-stock" && (
                    <SelectField
                        label="Replacement grip"
                        value={form.replacementGripId}
                        onChange={(value) =>
                            update(
                                "replacementGripId",
                                value
                            )
                        }
                        options={replacementGrips}
                        loading={loadingInventory}
                    />
                )}
            </div>

            <div>
                <h3 className="text-xl font-semibold">
                    Butt cap or end cap replacement
                </h3>

                <SimpleSelect
                    label="Butt-cap option"
                    value={form.buttCapService}
                    onChange={(value) =>
                        update("buttCapService", value)
                    }
                    options={[
                        {
                            value: "none",
                            label: "No butt-cap service",
                        },
                        {
                            value: "customer-provided",
                            label:
                                "I will provide the compatible part — +$10 labor",
                        },
                        {
                            value: "special-order",
                            label:
                                "Framework needs to source the part",
                        },
                    ]}
                />

                {form.buttCapService === "special-order" && (
                    <button
                        type="button"
                        onClick={() =>
                            openSpecialOrder("butt-cap")
                        }
                        className="mt-4 text-[#a6ce39] underline"
                    >
                        Submit butt-cap inquiry
                    </button>
                )}
            </div>

            <div>
                <Checkbox
                    checked={form.leadTapeRequest}
                    onChange={(checked) =>
                        update("leadTapeRequest", checked)
                    }
                    label="Request lead-tape installation"
                />

                {form.leadTapeRequest && (
                    <TextArea
                        label="How much weight and where should it be placed?"
                        value={form.leadTapeDetails}
                        onChange={(value) =>
                            update(
                                "leadTapeDetails",
                                value
                            )
                        }
                        placeholder="Example: 2 grams total at 3 and 9 o’clock. Framework will review the request and contact you before charging."
                    />
                )}
            </div>

            <div className="border border-[#a6ce39]/40 bg-[#a6ce39]/5 p-6 rounded-sm">
    <div className="fw-label mb-3">
        The Lab by Framework
    </div>

    <h3 className="fw-display text-3xl">
        Performance Racquet Customization Session
    </h3>

    <div className="text-2xl fw-display text-[#a6ce39] mt-4">
        $100 + Cost of Chosen Modifications
    </div>

    <p className="mt-4 text-white/80 font-medium">
        On-Court Evaluation → Racquet Customization → Play-Test &amp; Fine-Tuning
    </p>

    <p className="mt-4 text-white/60">
        A personalized racquet performance consultation designed to optimize your equipment for your swing, playing style, and performance goals.
    </p>

    <div className="flex flex-wrap gap-4 mt-8">
        <a
            href="/framework/services#performance-racquet-customization"
            className="fw-btn-primary"
        >
            Book It
        </a>

        <a
            href="/framework/services#performance-racquet-customization"
            className="px-5 py-3 border border-white/20 text-white hover:border-[#a6ce39] hover:text-[#a6ce39] transition"
        >
            Learn More
        </a>
    </div>
</div>
        </>
    );
}

function FormSection({ number, title, children }) {
    return (
        <section className="border-b border-white/10 pb-10 last:border-b-0">
            <div className="flex gap-3 items-center mb-6">
                <span className="fw-mono text-xs text-[#a6ce39]">
                    {number}
                </span>

                <h2 className="fw-display text-2xl sm:text-3xl">
                    {title}
                </h2>
            </div>

            {children}
        </section>
    );
}

function Field({
    label,
    value,
    onChange,
    testid,
    type = "text",
    required = false,
}) {
    return (
        <label className="block">
            <div className="fw-label mb-2">{label}</div>

            <input
                type={type}
                value={value}
                required={required}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                data-testid={testid}
                className="fw-input"
            />
        </label>
    );
}

function TextArea({
    label,
    value,
    onChange,
    placeholder = "",
}) {
    return (
        <label className="block mt-5">
            <div className="fw-label mb-2">{label}</div>

            <textarea
                rows={4}
                value={value}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                placeholder={placeholder}
                className="w-full bg-transparent border border-white/20 focus:border-[#a6ce39] outline-none text-white p-3 fw-mono text-sm"
            />
        </label>
    );
}

function ChoiceCard({
    title,
    description,
    selected,
    onClick,
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                "text-left border p-5 min-h-[120px] transition",
                selected
                    ? "border-[#a6ce39] bg-[#a6ce39]/10"
                    : "border-white/15 hover:border-white/30",
            ].join(" ")}
        >
            <div className="font-medium">{title}</div>

            {description && (
                <p className="text-sm text-white/50 mt-2">
                    {description}
                </p>
            )}
        </button>
    );
}

function Checkbox({
    checked,
    onChange,
    label,
    testid,
}) {
    return (
        <label className="flex items-start gap-3 cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) =>
                    onChange(event.target.checked)
                }
                data-testid={testid}
                className="mt-1 h-4 w-4 accent-[#a6ce39]"
            />

            <span className="text-sm text-white/70">
                {label}
            </span>
        </label>
    );
}


function PriceDisplay({ item }) {
    const regular = Number(
        item?.installed_price ?? item?.price ?? 0
    );
    const effective = getEffectivePrice(item);
    const onSale =
        item?.sale_active &&
        item?.sale_price != null &&
        Number(item.sale_price) < regular;

    if (!onSale) {
        return (
            <span className="shrink-0 fw-mono text-xs text-[#a6ce39]">
                +{formatMoney(effective)}
            </span>
        );
    }

    return (
        <span className="shrink-0 text-right">
            <span className="block fw-mono text-[10px] text-white/35 line-through">
                +{formatMoney(regular)}
            </span>
            <span className="block fw-mono text-xs text-[#a6ce39]">
                Sale +{formatMoney(effective)}
            </span>
        </span>
    );
}

function StringPicker({
    value,
    onChange,
    options,
    loading,
}) {
    const groups = [
        {
            id: "synthetic",
            label: "Synthetic Gut",
        },
        {
            id: "multi",
            label: "Multifilament",
        },
        {
            id: "poly",
            label: "Polyester",
        },
    ];

    if (loading) {
        return (
            <div className="mt-6 text-sm text-white/50">
                Loading string inventory…
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="fw-label mb-3">Premium string</div>

            <div className="border border-white/15 bg-black/20">
                {groups.map((group) => {
                    const groupItems = options.filter(
                        (item) =>
                            normalize(item.string_type) ===
                            normalize(group.id)
                    );

                    if (!groupItems.length) return null;

                    return (
                        <div key={group.id}>
                            <div className="border-b border-white/10 bg-white/[0.03] px-4 py-2 fw-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                                {group.label}
                            </div>

                            <div className="divide-y divide-white/10">
                                {groupItems.map((item) => {
                                    const id = String(
                                        item.id ?? item._id
                                    );
                                    const selected =
                                        String(value) === id;

                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() =>
                                                onChange(id)
                                            }
                                            className={[
                                                "flex w-full items-center gap-4 px-4 py-3 text-left transition",
                                                selected
                                                    ? "bg-[#a6ce39]/10 ring-1 ring-inset ring-[#a6ce39]"
                                                    : "hover:bg-white/[0.04]",
                                            ].join(" ")}
                                        >
                                            <div className="h-12 w-12 shrink-0 overflow-hidden border border-white/10 bg-[#111]">
                                                {item.image ? (
                                                    <img
                                                        src={item.image}
                                                        alt=""
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div
                                                        className="h-full w-full"
                                                        style={{
                                                            backgroundColor:
                                                                item.color_hex ||
                                                                "#222222",
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium text-white">
                                                    {[item.brand_name, item.name]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                </div>

                                                <div className="mt-1 text-xs text-white/50">
                                                    {[
                                                        item.shape
                                                            ? capitalize(item.shape)
                                                            : "",
                                                        item.gauge
                                                            ? `${item.gauge} gauge`
                                                            : "",
                                                        item.color,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </div>
                                            </div>

                                            <PriceDisplay item={item} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {!options.length && (
                    <div className="px-4 py-5 text-sm text-white/45">
                        No premium strings are currently available.
                    </div>
                )}
            </div>
        </div>
    );
}

function OvergripPicker({
    value,
    onChange,
    options,
    loading,
}) {
    const groups = [
        { id: "tacky", label: "Tacky" },
        { id: "dry", label: "Dry" },
    ];

    if (loading) {
        return (
            <div className="mt-6 text-sm text-white/50">
                Loading overgrips…
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="fw-label mb-3">
                Installed overgrip
            </div>

            <div className="border border-white/15 bg-black/20">
                {groups.map((group) => {
                    const groupItems = options.filter(
                        (item) =>
                            normalize(item.grip_type) ===
                            normalize(group.id)
                    );

                    if (!groupItems.length) return null;

                    return (
                        <div key={group.id}>
                            <div className="border-b border-white/10 bg-white/[0.03] px-4 py-2 fw-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                                {group.label}
                            </div>

                            <div className="divide-y divide-white/10">
                                {groupItems.map((item) => {
                                    const id = String(
                                        item.id ?? item._id
                                    );
                                    const selected =
                                        String(value) === id;

                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() =>
                                                onChange(id)
                                            }
                                            className={[
                                                "flex w-full items-center gap-3 px-4 py-3 text-left transition",
                                                selected
                                                    ? "bg-[#a6ce39]/10 ring-1 ring-inset ring-[#a6ce39]"
                                                    : "hover:bg-white/[0.04]",
                                            ].join(" ")}
                                        >
                                            <span
                                                className="h-5 w-10 shrink-0 rounded-sm border border-white/20"
                                                style={{
                                                    backgroundColor:
                                                        item.color_hex ||
                                                        "#ffffff",
                                                }}
                                                aria-hidden="true"
                                            />

                                            <span className="min-w-0 flex-1 truncate text-sm text-white/80">
                                                {item.description ||
                                                    [item.brand_name, item.name]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                            </span>

                                            <span className="shrink-0 text-xs text-white/45">
                                                {item.color}
                                            </span>

                                            <PriceDisplay item={item} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {!options.length && (
                    <div className="px-4 py-5 text-sm text-white/45">
                        No installed overgrips are currently available.
                    </div>
                )}
            </div>
        </div>
    );
}

function DampenerPicker({
    value,
    onChange,
    options,
    loading,
}) {
    if (loading) {
        return (
            <div className="mt-6 text-sm text-white/50">
                Loading dampeners…
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="fw-label mb-3">Dampener</div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {options.map((item) => {
                    const id = String(item.id ?? item._id);
                    const selected = String(value) === id;

                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => onChange(id)}
                            className={[
                                "overflow-hidden border bg-black/20 text-left transition",
                                selected
                                    ? "border-[#a6ce39] bg-[#a6ce39]/10"
                                    : "border-white/15 hover:border-white/35",
                            ].join(" ")}
                        >
                            <div className="aspect-square overflow-hidden bg-[#111]">
                                {item.image ? (
                                    <img
                                        src={item.image}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="h-full w-full"
                                        style={{
                                            backgroundColor:
                                                item.color_hex ||
                                                "#222222",
                                        }}
                                    />
                                )}
                            </div>

                            <div className="p-3">
                                <p className="line-clamp-2 min-h-[2.5rem] text-xs leading-5 text-white/70">
                                    {item.description ||
                                        [item.brand_name, item.name]
                                            .filter(Boolean)
                                            .join(" ")}
                                </p>

                                <div className="mt-3 flex justify-end">
                                    <PriceDisplay item={item} />
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {!options.length && (
                <div className="border border-white/15 px-4 py-5 text-sm text-white/45">
                    No dampeners are currently available.
                </div>
            )}
        </div>
    );
}

function SelectField({
    label,
    value,
    onChange,
    options,
    loading,
}) {
    return (
        <label className="block mt-6">
            <div className="fw-label mb-2">{label}</div>

            <select
                value={value}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                className="fw-select"
                disabled={loading}
            >
                <option value="">
                    {loading
                        ? "Loading inventory…"
                        : "Select an option"}
                </option>

                {options.map((item) => (
                    <option
                        key={item.id ?? item._id}
                        value={item.id ?? item._id}
                    >
                        {item.name} —{" "}
                        {formatMoney(
                            item.installed_price ??
                            item.price
                        )}
                    </option>
                ))}
            </select>
        </label>
    );
}

function SimpleSelect({
    label,
    value,
    onChange,
    options,
}) {
    return (
        <label className="block mt-5">
            <div className="fw-label mb-2">{label}</div>

            <select
                value={value}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                className="fw-select"
            >
                <option value="">Select an option</option>

                {options.map((option) => {
                    const item =
                        typeof option === "string"
                            ? {
                                  value: option,
                                  label: option,
                              }
                            : option;

                    return (
                        <option
                            key={item.value}
                            value={item.value}
                        >
                            {item.label}
                        </option>
                    );
                })}
            </select>
        </label>
    );
}

function SummaryRow({
    label,
    value,
    strong = false,
}) {
    return (
        <div
            className={[
                "flex justify-between gap-4",
                strong
                    ? "text-lg font-semibold"
                    : "text-sm",
            ].join(" ")}
        >
            <span className="text-white/60">
                {label}
            </span>

            <span>{value}</span>
        </div>
    );
}

function productsByCategory(products, category) {
    return products.filter(
        (item) =>
            normalize(item.category) === normalize(category) &&
            item.active !== false &&
            item.enabled !== false
    );
}

function findProduct(products, id) {
    return products.find(
        (item) =>
            String(item.id ?? item._id) === String(id)
    );
}

function normalize(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replaceAll(" ", "_")
        .replaceAll("-", "_");
}

function isInStock(product) {
    const status = normalize(
        product.stock_status ?? product.status
    );

    if (
        status === "out_of_stock" ||
        status === "special_order"
    ) {
        return false;
    }

    if (product.quantity != null) {
        return Number(product.quantity) > 0;
    }

    if (product.stock != null) {
        return Number(product.stock) > 0;
    }

    return product.in_stock !== false && product.enabled !== false;
}

function getEffectivePrice(item) {
    if (!item) return 0;

    const regular = Number(
        item.installed_price ?? item.price ?? 0
    );

    if (
        item.sale_active &&
        item.sale_price != null &&
        Number(item.sale_price) >= 0
    ) {
        return Number(item.sale_price);
    }

    return regular;
}

function capitalize(value) {
    const text = String(value || "");
    return text
        ? text.charAt(0).toUpperCase() + text.slice(1)
        : "";
}

function formatMoney(value) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(Number(value || 0));
}
